/**
 * A single row in the shipments table. Shows details for a single incomingShipment.
 */

import React, { useState } from "react";
import Column from "@amzn/meridian/column";
import { TableRow, TableCell } from "@amzn/meridian/table";
import PropTypes from "prop-types";

import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Link from "@amzn/meridian/link";
import Text from "@amzn/meridian/text";
import Tooltip from "@amzn/meridian/tooltip";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import copyTokens from "@amzn/meridian-tokens/base/icon/copy";

import RiverModal from "../river/river-modal";
import incomingShipmentPropType from "../../prop-types/incoming-shipment";
import useIncomingShipmentsViewPreferences from "../../hooks/use-incoming-shipments-view-preferences";
import useWarehouseData from "../../hooks/use-warehouse-data";
import { convertUnix } from "../../utilities/convert-unix";
import { getWarehouseAttributes } from "../../utilities/get-warehouse-attributes";
import {
  getVendorLink,
  getTransshipmentLink,
  getProcurementURL,
} from "../../utilities/links";
import Row from "@amzn/meridian/row";
import Alert from "@amzn/meridian/alert";
import Toaster from "@amzn/meridian/toaster";
import ExceptionTag from "../app/exception-tag";
import {
  loadIdDisplayLength,
  unscheduledVendorName,
  notYetManifestedName,
} from "../Constants";
import EventIcon from "../event/event-icon";

const IncomingShipmentsUnscheduledTableRow = (props) => {
  const { incomingShipment, warehouseId } = props;

  const warehouseData = useWarehouseData();
  const { business_type, region_id, time_zone, wms } =
    getWarehouseAttributes({
      warehouseId: warehouseId,
      warehouseParams: warehouseData,
    });

  const [viewPreferences] = useIncomingShipmentsViewPreferences();

  const visibleColumns = viewPreferences.get("columns");
  const [expandRow, setExpandrow] = useState(false);

  const [toasts, setToasts] = useState([]);
  const onCloseToast = (id) => setToasts(toasts.filter((t) => t.id !== id));
  const onOpenToast = (loadId) =>
    setToasts(toasts.concat({ id: loadId, timeout: 1500 }));

  // Map to correct worflow based on site parameters
  // TODO: See if we can plug in parameters
  const workflowMap = {
    1: {
      Mendel: [
        {
          title: "Failed Temp Test",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=d7ed5d53-1189-4238-9231-19e455217d2d&id=d7ed5d53-1189-4238-9231-19e455217d2d",
        },
        {
          title: "Pallet Too Tall",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=a10a0c11-67fe-4f49-9b33-62be79d09b0d&id=a10a0c11-67fe-4f49-9b33-62be79d09b0d",
        },
        {
          title: "Wrong Stores Pallet",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=c47eb9ef-af9d-442a-8d4a-dd08914b92f5&id=c47eb9ef-af9d-442a-8d4a-dd08914b92f5",
        },
        {
          title: "Early Delivery Arrivals",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=ee7d411e-545e-429b-8c27-d3864d0fc0f1&id=ee7d411e-545e-429b-8c27-d3864d0fc0f1",
        },
        {
          title: "Other",
          url: "https://river.amazon.com/?org=amznmndl&buildingId=SEA53&q0=d4775f0b-b726-407d-94db-64e703c3a8cb",
        },
      ],
      Malone: [
        {
          title: "Failed Temp Test",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=d7ed5d53-1189-4238-9231-19e455217d2d&id=d7ed5d53-1189-4238-9231-19e455217d2d",
        },
        {
          title: "Pallet Too Tall",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=a10a0c11-67fe-4f49-9b33-62be79d09b0d&id=a10a0c11-67fe-4f49-9b33-62be79d09b0d",
        },
        {
          title: "Wrong Stores Pallet",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=c47eb9ef-af9d-442a-8d4a-dd08914b92f5&id=c47eb9ef-af9d-442a-8d4a-dd08914b92f5",
        },
        {
          title: "Early Delivery Arrivals",
          url: "https://river.amazon.com/SEA53/workflows?buildingType=amznmndl&q0=d4775f0b-b726-407d-94db-64e703c3a8cb&q1=ee7d411e-545e-429b-8c27-d3864d0fc0f1&id=ee7d411e-545e-429b-8c27-d3864d0fc0f1",
        },
        {
          title: "Other",
          url: "https://river.amazon.com/?org=amznmndl&buildingId=SEA53&q0=d4775f0b-b726-407d-94db-64e703c3a8cb",
        },
      ],
      UFF: [
        {
          title: "PO Rejections",
          url: "https://river.amazon.com/BOS21/workflows?buildingType=UFFSCExecution&q0=2fad020b-108a-493c-b733-85b8d938956f&id=2fad020b-108a-493c-b733-85b8d938956f",
        },
        // {
        //   "title": "Full Pallet Short",
        //   "url": "
        // },
        {
          title: "PO not in AFTLite/Receive",
          url: "https://river.amazon.com/BOS21/workflows?buildingType=UFFSCExecution&q0=d9064ab3-7b54-4dc3-8f8d-3a530f438b39&id=d9064ab3-7b54-4dc3-8f8d-3a530f438b39",
        },
        {
          title: "Vendor/Whole Foods Compliance",
          url: "https://river.amazon.com/BOS21/workflows?buildingType=UFFSCExecution&q0=d167d18f-8a95-4bfc-9b87-83f15a87e7a5&id=d167d18f-8a95-4bfc-9b87-83f15a87e7a5",
        },
        {
          title: "Other",
          url: "https://river.amazon.com/BOS21/home?buildingType=UFFSCExecution",
        },
      ],
      SSD: [
        {
          "title": "Vendor Compliance",
          "url": "https://river.amazon.com/?org=SSD_SC&q0=90c05273-bc82-4cbb-bc61-f3210ce2c03f&q1=72568353-ad8b-400b-93bf-7abd588d3f4e"
        },
        {
          "title": "DC Compliance",
          "url": "https://river.amazon.com/?org=SSD_SC&q0=90c05273-bc82-4cbb-bc61-f3210ce2c03f&q1=0719a240-b3d7-473f-9970-dede655d16a2"
        },
        {
          "title": "Rejections",
          "url": "https://river.amazon.com/GYR2/workflows?buildingType=SSD_SC&q0=90c05273-bc82-4cbb-bc61-f3210ce2c03f&q1=84113b48-3af2-4a9b-819b-e070978ff5b1&id=84113b48-3af2-4a9b-819b-e070978ff5b1"
        }
      ],
      Go: [
        {
          "title": "Amazon Go Workflows",
          "url": "https://river.amazon.com/?org=amazon_go&q0=85914a4a-882a-498e-afb8-20c88c839da9"
        }
      ]
    },
    2: {
      UFF: [
        {
          title: "Other",
          url: "https://river.amazon.com/LHR16/home?buildingType=aft_lite_fc&q0=ce9abbea-c39e-4860-bc14-0154f653554b&q1=4962b4d4-23f5-4bdc-afd3-0bbff7f750a8",
        },
      ],
    },
    3: {
      UFF: [
        {
          title: "Other",
          url: "https://river.amazon.com/LHR16/home?buildingType=aft_lite_fc&q0=ce9abbea-c39e-4860-bc14-0154f653554b&q1=4962b4d4-23f5-4bdc-afd3-0bbff7f750a8",
        },
      ],
    },
  };

  const riverWorkflows = workflowMap[region_id][business_type]
    ? workflowMap[region_id][business_type]
    : [];

  return (
    <TableRow key={incomingShipment.id} rowComponents={props.rowComponents}>
      <Toaster toasts={toasts} onCloseToast={onCloseToast}>
        {(toast) => (
          <Alert toast={true} onClose={toast.onClose}>
            {`${toast.id} copied to clipboard`}
          </Alert>
        )}
      </Toaster>
      {visibleColumns.loadType && (
        <TableCell alignmentHorizontal="center" width={95}>
          <Text tag="p">{incomingShipment.loadType}</Text>
        </TableCell>
      )}
      {visibleColumns.source && (
        <TableCell alignmentHorizontal="center" width={95}>
          {incomingShipment.loadType === "Vendor" &&
            incomingShipment.distributorName ? (
            <Tooltip position="end" title={incomingShipment.distributorName}>
              <Text tag="p">{incomingShipment.source}</Text>
            </Tooltip>
          ) : (
            <Text tag="p">{incomingShipment.source}</Text>
          )}
        </TableCell>
      )}
      {visibleColumns.loadId && (
        <TableCell alignmentHorizontal="center">
          <Column>
            {typeof incomingShipment.loadId === "string" ? (
              [unscheduledVendorName, notYetManifestedName].includes(
                incomingShipment.loadId
              ) ? (
                <Text truncate={!expandRow}>{incomingShipment.loadId}</Text>
              ) : (
                <Row alignmentHorizontal="center">
                  <Tooltip
                    position="end"
                    title={incomingShipment.loadId}
                    id={`${incomingShipment.id}-${incomingShipment.loadId}-toolTip`}
                  >
                    <Link
                      href={
                        incomingShipment.loadType === "Vendor"
                          ? getVendorLink(
                            region_id,
                            wms,
                            incomingShipment.loadId
                          )
                          : getTransshipmentLink(
                            region_id,
                            wms,
                            incomingShipment.loadId
                          )
                      }
                      target={"_blank"}
                      rel={"noopener noreferrer"}
                    >
                      <Text truncate={!expandRow}>
                        {incomingShipment.loadId.length > loadIdDisplayLength
                          ? `${incomingShipment.loadId.substring(
                            0,
                            loadIdDisplayLength
                          )}...`
                          : incomingShipment.loadId}
                      </Text>
                    </Link>
                  </Tooltip>
                  <Button
                    type="icon"
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(incomingShipment.loadId);
                      onOpenToast(incomingShipment.loadId);
                    }}
                  >
                    <Icon tokens={copyTokens} />
                  </Button>
                </Row>
              )
            ) : incomingShipment.loadId.length > 0 ? (
              incomingShipment.loadId.map((loadId) =>
                loadId === "Not Yet Manifested" ? (
                  <Text truncate={!expandRow}>{loadId}</Text>
                ) : (
                  <Row>
                    <Tooltip
                      position="end"
                      title={incomingShipment.loadId}
                      id={`${incomingShipment.id}-${incomingShipment.loadId}-toolTip`}
                    >
                      <Link
                        href={
                          incomingShipment.loadType === "Vendor"
                            ? getProcurementURL(region_id, loadId)
                            : getTransshipmentLink(region_id, wms, loadId)
                        }
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                      >
                        <Text truncate={!expandRow}>
                          {loadId.length > loadIdDisplayLength
                            ? `${loadId.substring(0, loadIdDisplayLength)}...`
                            : loadId}
                        </Text>
                      </Link>
                    </Tooltip>
                    <Button
                      type="icon"
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(incomingShipment.loadId);
                        onOpenToast(incomingShipment.loadId);
                      }}
                    >
                      <Icon tokens={copyTokens} />
                    </Button>
                  </Row>
                )
              )
            ) : null}
            {incomingShipment.criticalAsinEvents && (
              <Row alignmentHorizontal="center">
                {incomingShipment.criticalAsinEvents.map((criticalEvent) => (
                  <EventIcon
                    event={criticalEvent.eventName}
                    regionId={region_id}
                  />
                ))}
              </Row>
            )}
            {incomingShipment.arrivalDatetime[0] ? (
              <>
                <ExceptionTag shipment={{ exceptionId: "arrived" }} />
                <ExceptionTag shipment={{ exceptionId: "dockCheckin" }} />
              </>
            ) : null}
          </Column>
        </TableCell>
      )}
      {visibleColumns.tempZone && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">{incomingShipment.tempZone}</Text>
        </TableCell>
      )}
      {visibleColumns.status && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">{incomingShipment.status}</Text>
        </TableCell>
      )}

      {/* {visibleColumns.estimatedArrivalTime && (
        <TableCell alignmentHorizontal="center">
          <Text>
            {incomingShipment.scheduledArrivals.length > 0 && incomingShipment.loadType==="Vendor"
              ? convertUnix(incomingShipment.scheduledArrivals[0], region_id === 1 ? "America/Los_Angeles" : time_zone) 
              : convertUnix(incomingShipment.firstAppointmentTime, time_zone)}
          </Text>
        </TableCell>
      )} */}

      {visibleColumns.handoffWindowStartDateUTC && (
        <TableCell alignmentHorizontal="center">
          <Text>
            {incomingShipment.handoffWindowStartDateUTC
              ? convertUnix(
                incomingShipment.handoffWindowStartDateUTC,
                region_id === 1 ? "America/Los_Angeles" : time_zone)
              : convertUnix(incomingShipment.departureTime, time_zone)}
          </Text>
        </TableCell>
      )}

      {visibleColumns.handoffWindowEndDateUTC && (
        <TableCell alignmentHorizontal="center">
          <Text>
            {incomingShipment.handoffWindowEndDateUTC
              ? convertUnix(incomingShipment.handoffWindowEndDateUTC, region_id === 1 ? "America/Los_Angeles" : time_zone)
              : convertUnix(incomingShipment.firstAppointmentTime, time_zone)}
          </Text>
        </TableCell>
      )}

      {visibleColumns.expectedUnits && (
        <TableCell alignmentHorizontal="center">
          <Tooltip
            position="end"
            title={
              <div style={{ whiteSpace: "pre-line" }}>
                {`Source: ${incomingShipment.expectedUnitSource}`
                  .split(", ")
                  .join("\n")}
              </div>
            }
            id={`${incomingShipment.id}-${incomingShipment.tempZone}-expectedUnitSource-toolTip`}
          >
            <Text tag="p">
              {incomingShipment.expectedUnits
                ? incomingShipment.expectedUnits.toLocaleString()
                : 0}
            </Text>
          </Tooltip>
        </TableCell>
      )}
      {visibleColumns.expectedAsrsUnits && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">
            {incomingShipment.expectedAsrsUnits
              ? incomingShipment.expectedAsrsUnits.toLocaleString()
              : 0}
          </Text>
        </TableCell>
      )}
      {visibleColumns.expectedAsrsUnitsPercent && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">
            {incomingShipment.expectedAsrsUnitsPercent
              ? `${incomingShipment.expectedAsrsUnitsPercent.toLocaleString()}%`
              : `0%`}
          </Text>
        </TableCell>
      )}
      {/* {visibleColumns.expectedPallets && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">
            {incomingShipment.expectedPallets
              ? incomingShipment.expectedPallets.toLocaleString()
              : 0}
          </Text>
        </TableCell>
      )} */}
      {visibleColumns.shipmentId && (
        <TableCell alignmentHorizontal="center">
          <Column>
            {incomingShipment.shipmentId.map((shipmentId) => (
              <Text tag="p">{shipmentId}</Text>
            ))}
            {<Text tag="p">{incomingShipment.stopSequence}</Text>}
          </Column>
        </TableCell>
      )}
      {visibleColumns.ocrVendor && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">{incomingShipment.ocrVendor}</Text>
        </TableCell>
      )}
      {visibleColumns.scac && (
        <TableCell alignmentHorizontal="center">
          <Text tag="p">{incomingShipment.scac}</Text>
        </TableCell>
      )}
      <TableCell alignmentHorizontal="center" width={65}>
        <Button
          type="icon"
          size="small"
          onClick={() => {
            expandRow ? setExpandrow(false) : setExpandrow(true);
          }}
        >
          {expandRow ? (
            <Icon tokens={chevronDownSmallTokens}>Collapse rows</Icon>
          ) : (
            <Icon tokens={chevronRightSmallTokens}>Expand rows</Icon>
          )}
        </Button>
        {expandRow && (
          <Column spacing="300">
            <RiverModal
              buttonTitle={"River"}
              data={incomingShipment}
              modalTitle={"River"}
              riverWorkflows={riverWorkflows}
            />
          </Column>
        )}
      </TableCell>
    </TableRow>
  );
};

IncomingShipmentsUnscheduledTableRow.propTypes = {
  incomingShipment: incomingShipmentPropType.isRequired,
  rowComponents: TableRow.propTypes.rowComponents,
  warehouseId: PropTypes.string.isRequired,
};

export default IncomingShipmentsUnscheduledTableRow;
